import { useEffect, useMemo, useState } from "react";
import { Download, ExternalLink, FileSpreadsheet, FileText, Link2, Loader2 } from "lucide-react";
import StatusPill from "./components/StatusPill";
import { useMerchantApplication } from "../../context/MerchantApplicationContext";
import merchantApi from "../../lib/merchantApi";

const TABS = [
  { key: "financial_reports", label: "Financial reports", emptyLine: "Your account manager will share financial reports here once you're live." },
  { key: "performance",       label: "Performance",       emptyLine: "Weekly performance reports from your account manager will land here." },
  { key: "proof_of_payment",  label: "Proof of payment",  emptyLine: "Bank wire receipts and crypto transaction links will appear here as your team makes payouts." },
];

const FORMAT_LABEL = {
  pdf: "PDF", doc: "DOC", docx: "DOCX", xls: "XLS", xlsx: "XLSX",
  csv: "CSV", jpg: "JPG", jpeg: "JPG", png: "PNG",
};

export default function FinancialsPage() {
  const { application } = useMerchantApplication();
  const [tab, setTab] = useState("financial_reports");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await merchantApi.get("/merchant/reports", { params: { tab } });
        if (!cancelled) setItems(data?.items || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tab]);

  const tabMeta = useMemo(() => TABS.find((t) => t.key === tab), [tab]);

  const download = async (report) => {
    setDownloadingId(report.report_id);
    try {
      const { data } = await merchantApi.post(`/merchant/reports/${report.report_id}/url`);
      const token = localStorage.getItem("neptune_merchant_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const resp = await fetch(data.url, { credentials: "include", headers });
      if (!resp.ok) throw new Error("download failed");
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = report.filename || "report";
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 30000);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div data-testid="financials-page" className="max-w-6xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">— Financials</div>
          <h2 className="font-display mt-2 text-3xl sm:text-4xl font-semibold tracking-tighter chrome-text leading-tight">
            Financials
          </h2>
        </div>
        {application && (
          <div className="flex flex-col items-end gap-1.5">
            <StatusPill status={application.status} />
            <div className="ref-mono text-[11px] text-zinc-500">{application.ref}</div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div data-testid="financials-tabs" className="mt-9 flex gap-1 border-b border-white/[0.06]">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            data-testid={`financials-tab-${t.key}`}
            className={`relative px-4 py-2.5 text-[13px] transition-colors ${
              tab === t.key ? "text-white" : "text-zinc-400 hover:text-white"
            }`}
          >
            {t.label}
            {tab === t.key && (
              <span className="absolute left-0 right-0 bottom-[-1px] h-[2px] bg-gradient-to-r from-[#00E5FF] to-[#0055FF]" />
            )}
          </button>
        ))}
      </div>

      {/* Body */}
      <div className="mt-7">
        {loading ? (
          <div data-testid="financials-loading" className="flex items-center gap-2 text-zinc-500 text-[12px] uppercase tracking-[0.22em]">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div
            data-testid={`financials-empty-${tab}`}
            className="rounded-2xl border border-dashed border-white/[0.10] bg-white/[0.015] p-10 text-center text-[13.5px] text-zinc-400"
          >
            {tabMeta.emptyLine}
          </div>
        ) : tab === "proof_of_payment" ? (
          /* Proof of payment — card layout, blends crypto-link + bank-file rows. */
          <ul className="space-y-3" data-testid="financials-list-proof_of_payment">
            {items.map((r) => (
              <li
                key={r.report_id}
                data-testid={`financials-row-${r.report_id}`}
                className="rounded-2xl border border-white/[0.06] bg-white/[0.015] hover:bg-white/[0.025] transition-colors p-5"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    {r.kind === "crypto_link" ? (
                      <div className="h-9 w-9 rounded-xl border border-amber-400/30 bg-amber-400/[0.08] flex items-center justify-center shrink-0">
                        <Link2 className="h-4 w-4 text-amber-200" />
                      </div>
                    ) : (
                      <div className="h-9 w-9 rounded-xl border border-cyan-400/30 bg-cyan-400/[0.06] flex items-center justify-center shrink-0">
                        <FileText className="h-4 w-4 text-[#00E5FF]" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                          {r.kind === "crypto_link" ? "Crypto" : "Bank"}
                        </span>
                        {r.payment_date && (
                          <span className="text-[11px] text-zinc-400 ref-mono">{r.payment_date}</span>
                        )}
                        {r.payment_amount && (
                          <span className="text-[12px] text-white font-mono">
                            {r.payment_amount}{r.payment_currency ? ` ${r.payment_currency}` : ""}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-[14px] text-white truncate" title={r.title}>{r.title}</div>
                      <div className="mt-0.5 text-[12px] text-zinc-500">{r.period}</div>
                      {r.kind === "crypto_link" && r.crypto_url && (
                        <a
                          href={r.crypto_url} target="_blank" rel="noopener noreferrer"
                          data-testid={`financials-crypto-${r.report_id}`}
                          className="mt-3 inline-flex items-center gap-1.5 text-[12px] text-cyan-300 hover:text-cyan-200 font-mono break-all"
                        >
                          <ExternalLink className="h-3 w-3 shrink-0" />
                          {r.crypto_url}
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <span className="text-[11px] text-zinc-500">
                      Posted {new Date(r.uploaded_at).toLocaleDateString()}
                    </span>
                    {r.kind !== "crypto_link" && (
                      <button
                        type="button"
                        onClick={() => download(r)}
                        disabled={downloadingId === r.report_id}
                        data-testid={`financials-download-${r.report_id}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.12] bg-white/[0.03] hover:bg-white/[0.08] text-white px-3 py-1.5 text-[11.5px] disabled:opacity-50"
                      >
                        <Download className="h-3 w-3" />
                        {downloadingId === r.report_id ? "Downloading…" : "Download"}
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.015] overflow-hidden">
            <table className="w-full text-left">
              <thead className="bg-white/[0.02] border-b border-white/[0.06]">
                <tr className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">
                  <th className="px-5 py-3">Title</th>
                  <th className="px-5 py-3">Period</th>
                  <th className="px-5 py-3">Format</th>
                  <th className="px-5 py-3">Uploaded</th>
                  <th className="px-5 py-3 w-32"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr
                    key={r.report_id}
                    data-testid={`financials-row-${r.report_id}`}
                    className="border-b border-white/[0.04] last:border-0 hover:bg-white/[0.025] transition-colors"
                  >
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2 min-w-0">
                        {r.ext === "csv" || r.ext === "xls" || r.ext === "xlsx"
                          ? <FileSpreadsheet className="h-3.5 w-3.5 text-emerald-300 shrink-0" />
                          : <FileText className="h-3.5 w-3.5 text-[#00E5FF] shrink-0" />}
                        <span className="text-[13px] text-white truncate" title={r.title}>{r.title}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-[12.5px] text-zinc-300">{r.period}</td>
                    <td className="px-5 py-3.5">
                      <span className="ref-mono inline-flex items-center justify-center rounded-md bg-white/[0.04] border border-white/[0.06] px-1.5 py-0.5 text-[10.5px] tracking-[0.1em] text-zinc-300">
                        {FORMAT_LABEL[r.ext] || (r.ext || "—").toUpperCase()}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-[12px] text-zinc-500">
                      {new Date(r.uploaded_at).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <button
                        type="button"
                        onClick={() => download(r)}
                        disabled={downloadingId === r.report_id}
                        data-testid={`financials-download-${r.report_id}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.10] bg-white/[0.02] hover:bg-white/[0.06] text-white px-3 py-1.5 text-[11.5px] disabled:opacity-50"
                      >
                        <Download className="h-3 w-3" />
                        {downloadingId === r.report_id ? "Downloading…" : "Download"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
